import { useEffect, useRef } from 'preact/hooks';
import { voiceState, micLevel, warmRemaining, tapVoice, startListening, stopListening } from '@/state/voice';
import { ttsMuted, setTtsMuted } from '@/lib/tts';
import { Icon } from '@/components/Icon';

const STATE_LABEL: Record<string, string> = {
  idle: 'Tap to speak',
  listening: 'Listening',
  processing: 'Thinking',
  speaking: 'Speaking',
  warm: 'Still listening',
};

/** Live 5-bar waveform driven by real mic level (anim #2). */
function Waveform() {
  const bars = [0.6, 1.0, 0.45, 0.85, 0.7];
  const refs = useRef<(HTMLElement | null)[]>([]);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const lvl = Math.min(1, micLevel.value * 6);
      refs.current.forEach((el, i) => {
        if (el) el.style.transform = `scaleY(${0.35 + lvl * bars[i] * (0.8 + 0.4 * Math.sin(Date.now() / 90 + i))})`;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <span class="vwave" aria-hidden="true">
      {bars.map((_, i) => (
        <i
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
        />
      ))}
    </span>
  );
}

/** The hero voice control: 5 visual states per the design contract.
 *  Large (Chat tab) and fab (other screens) sizes share this component. */
export function VoiceButton({ size = 'large' }: { size?: 'large' | 'fab' }) {
  const state = voiceState.value;
  const px = size === 'large' ? (state === 'listening' ? 84 : 64) : 52;

  // push-to-talk: long-press holds the mic open, release sends
  const holdTimer = useRef<ReturnType<typeof setTimeout>>();
  const held = useRef(false);

  const onPointerDown = () => {
    held.current = false;
    if (state === 'idle' || state === 'warm') {
      holdTimer.current = setTimeout(() => {
        held.current = true;
        void startListening();
      }, 450);
    }
  };
  const onPointerUp = () => {
    clearTimeout(holdTimer.current);
    if (held.current) {
      held.current = false;
      void stopListening();
    } else {
      tapVoice();
    }
  };

  return (
    <div class={`voice-wrap voice-wrap--${size}`}>
      {size === 'large' && state !== 'idle' && (
        <div class={`voice-eyebrow voice-eyebrow--${state}`} role="status">
          <span class="voice-eyebrow__dot" />
          {STATE_LABEL[state]}
        </div>
      )}
      <button
        class={`voice-btn voice-btn--${state}`}
        style={{ width: `${px}px`, height: `${px}px` }}
        type="button"
        aria-label={`Voice. ${STATE_LABEL[state]}.`}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={() => clearTimeout(holdTimer.current)}
      >
        {state === 'speaking' && (
          <>
            <span class="vripple" />
            <span class="vripple vripple--late" />
          </>
        )}
        {state === 'warm' && (
          <svg class="vring" viewBox="0 0 36 36" aria-hidden="true">
            <circle
              cx="18" cy="18" r="16.5"
              fill="none" stroke="rgba(255,255,255,.85)" stroke-width="2.4"
              stroke-linecap="round"
              stroke-dasharray={`${(warmRemaining.value * 103.7).toFixed(1)} 103.7`}
              transform="rotate(-90 18 18)"
            />
          </svg>
        )}
        {state === 'listening' ? (
          <Waveform />
        ) : state === 'processing' ? (
          <span class="varc" aria-hidden="true" />
        ) : state === 'speaking' ? (
          <Icon name="speaker" size={size === 'large' ? 26 : 20} />
        ) : (
          <Icon name="mic" size={size === 'large' ? 26 : 20} />
        )}
      </button>
      {size === 'large' && state === 'speaking' && (
        <button class="voice-mute" onClick={() => setTtsMuted(!ttsMuted.value)} aria-label={ttsMuted.value ? 'Unmute' : 'Mute'}>
          <Icon name={ttsMuted.value ? 'speaker-off' : 'speaker'} size={16} />
          {ttsMuted.value ? 'Muted' : 'Mute'}
        </button>
      )}
    </div>
  );
}
